import {
	Editor,
	StateNode,
	TLClickEventInfo,
	TLHandle,
	TLNoteShape,
	TLPointerEventInfo,
	Vec,
} from '@tldraw/editor'
import {
	clearArrowTargetState,
	updateArrowTargetState,
} from '../../../shapes/arrow/arrowTargetState'
import { getArrowBindings } from '../../../shapes/arrow/shared'
import {
	getNoteAdjacentPositions,
	getNoteShapeForAdjacentPosition,
	startEditingAdjacentNote,
} from '../../../shapes/note/noteHelpers'
import type { NoteShapeUtil } from '../../../shapes/note/NoteShapeUtil'
import { getDisplayValues } from '../../../shapes/shared/getDisplayValues'
import { DeferredDoubleClick, startEditingShapeWithRichText } from '../selectHelpers'

export class PointingHandle extends StateNode {
	static override id = 'pointing_handle'

	didCtrlOnEnter = false

	info = {} as TLPointerEventInfo & { target: 'handle' }
	private doubleClick = new DeferredDoubleClick(this)

	override onEnter(info: TLPointerEventInfo & { target: 'handle' }) {
		this.info = info
		this.doubleClick.start(info)

		this.didCtrlOnEnter = info.accelKey

		const { shape } = info
		if (this.editor.isShapeOfType(shape, 'arrow')) {
			const initialBindings = getArrowBindings(this.editor, shape)
			const currentBinding = initialBindings[info.handle.id as 'start' | 'end']
			const oppositeBinding = initialBindings[info.handle.id === 'start' ? 'end' : 'start']
			const arrowTransform = this.editor.getShapePageTransform(shape.id)!

			if (currentBinding) {
				updateArrowTargetState({
					editor: this.editor,
					pointInPageSpace: arrowTransform.applyToPoint(info.handle),
					arrow: shape,
					isPrecise: currentBinding.props.isPrecise,
					currentBinding: currentBinding,
					oppositeBinding: oppositeBinding,
				})
			}
		}

		this.editor.setCursor({ type: 'grabbing', rotation: 0 })
	}

	override onExit() {
		this.editor.setHintingShapes([])
		// onEnter shows the arrow's binding target; a click without a drag would leave it showing
		clearArrowTargetState(this.editor)
		this.editor.setCursor({ type: 'default', rotation: 0 })
	}

	override onPointerUp() {
		const { shape, handle } = this.info

		// The shape may have been deleted since pointer down (remote user, undo); the note
		// branch below would clone a new note from the dead record
		if (!this.editor.getShape(shape.id)) {
			this.parent.transition('idle')
			return
		}

		if (this.doubleClick.replay()) return

		if (this.editor.isShapeOfType(shape, 'note')) {
			const { editor } = this
			const nextNote = getNoteForAdjacentPosition(editor, shape, handle, false)
			if (nextNote) {
				startEditingAdjacentNote(editor, nextNote)
				return
			}
		}

		this.parent.transition('idle', this.info)
	}

	override onDoubleClick(info: TLClickEventInfo) {
		this.doubleClick.defer(info)
	}

	override onPointerMove(info: TLPointerEventInfo) {
		const { editor } = this
		if (editor.inputs.getIsDragging()) {
			if (this.didCtrlOnEnter) {
				this.parent.transition('brushing', info)
			} else {
				this.startDraggingHandle()
			}
		}
	}

	override onLongPress() {
		this.startDraggingHandle()
	}

	private startDraggingHandle() {
		const { editor } = this
		if (editor.getIsReadonly()) return
		const { shape, handle } = this.info

		if (!editor.getShape(shape.id)) {
			this.parent.transition('idle')
			return
		}

		if (editor.isShapeOfType(shape, 'note')) {
			const noteUtil = editor.getShapeUtil(shape) as NoteShapeUtil
			const dv = getDisplayValues(noteUtil, shape)

			// Cancelling the drag bails to this mark, which removes the note created below
			const creatingMarkId = editor.markHistoryStoppingPoint('creating note from handle')
			const nextNote = getNoteForAdjacentPosition(editor, shape, handle, true)
			if (nextNote) {
				// Center the shape on the current pointer
				const centeredOnPointer = editor
					.getPointInParentSpace(nextNote, editor.inputs.getOriginPagePoint())
					.sub(
						Vec.Rot(
							new Vec(dv.noteWidth / 2, dv.noteHeight / 2).mul(shape.props.scale),
							nextNote.rotation
						)
					)
				editor.updateShape({ ...nextNote, x: centeredOnPointer.x, y: centeredOnPointer.y })

				// Then select and begin translating the shape
				editor
					.setHoveredShape(nextNote.id) // important!
					.select(nextNote.id)
					.setCurrentTool('select.translating', {
						...this.info,
						target: 'shape',
						shape: editor.getShape(nextNote),
						isCreating: true,
						creatingMarkId,
						onCreate: () => {
							// When we're done, start editing it
							startEditingShapeWithRichText(editor, nextNote, { selectAll: true })
						},
					})
				return
			}
		}

		this.parent.transition('dragging_handle', this.info)
	}

	override onCancel() {
		this.cancel()
	}

	override onComplete() {
		this.cancel()
	}

	override onInterrupt() {
		this.cancel()
	}

	private cancel() {
		this.parent.transition('idle')
	}
}

function getNoteForAdjacentPosition(
	editor: Editor,
	shape: TLNoteShape,
	handle: TLHandle,
	forceNew: boolean
) {
	const noteUtil = editor.getShapeUtil(shape) as NoteShapeUtil
	const dv = getDisplayValues(noteUtil, shape)

	const pageTransform = editor.getShapePageTransform(shape.id)!
	const pagePoint = pageTransform.point()
	const pageRotation = pageTransform.rotation()
	const positions = getNoteAdjacentPositions(editor, {
		pagePoint,
		pageRotation,
		growY: shape.props.growY * shape.props.scale,
		extraHeight: 0,
		scale: shape.props.scale,
		noteWidth: dv.noteWidth,
		noteHeight: dv.noteHeight,
	})
	const position = positions[handle.index]
	if (position) {
		return getNoteShapeForAdjacentPosition(editor, {
			shape,
			center: position,
			pageRotation,
			noteWidth: dv.noteWidth,
			noteHeight: dv.noteHeight,
			forceNew,
		})
	}
	return undefined
}
